import { onBeforeUnmount, watch, type Ref } from 'vue';
import type { DockPane } from '../lib/dockTabs';

type ActiveResize = { side: DockPane['side']; start: number; startSize: number; pointerId: number; handle: HTMLElement; span: number };

export function dockSizeAfterMove(resize: Pick<ActiveResize, 'side' | 'start' | 'startSize' | 'span'>, clientX: number, clientY: number) {
  const delta = resize.side === 'right' ? resize.start - clientX : resize.start - clientY;
  return Math.min(.75, Math.max(.25, resize.startSize + delta / resize.span));
}

export function useDockResize(detachedDock: Ref<DockPane | null>) {
  let active: ActiveResize | null = null;

  function resizeDock(event: PointerEvent) {
    if (!active || !detachedDock.value || event.pointerId !== active.pointerId) return;
    detachedDock.value.size = dockSizeAfterMove(active, event.clientX, event.clientY);
  }
  function stopDockResize(event?: Event) {
    const current = active;
    if (!current || (typeof PointerEvent !== 'undefined' && event instanceof PointerEvent && event.pointerId !== current.pointerId)) return;
    active = null;
    document.body.classList.remove('dock-resizing');
    if (current.handle.hasPointerCapture(current.pointerId)) current.handle.releasePointerCapture(current.pointerId);
  }
  function startDockResize(event: PointerEvent) {
    if (!detachedDock.value || event.button !== 0) return;
    stopDockResize();
    const handle = event.currentTarget as HTMLElement;
    const workbench = handle.closest<HTMLElement>('.workbench');
    const span = detachedDock.value.side === 'right' ? workbench?.clientWidth : workbench?.clientHeight;
    if (!span) return;
    handle.setPointerCapture(event.pointerId);
    active = { side: detachedDock.value.side, start: detachedDock.value.side === 'right' ? event.clientX : event.clientY, startSize: detachedDock.value.size, pointerId: event.pointerId, handle, span };
    document.body.classList.add('dock-resizing');
  }
  watch(detachedDock, (dock) => { if (!dock) stopDockResize(); });
  onBeforeUnmount(() => stopDockResize());
  return { resizeDock, stopDockResize, startDockResize };
}
